import type { MeasureText } from "./fit-label";

let context: CanvasRenderingContext2D | null | undefined;

function getContext() {
  if (context === undefined) {
    try {
      context = document.createElement("canvas").getContext("2d");
    } catch {
      context = null;
    }
  }
  return context;
}

/**
 * A text measurer in `element`'s own font, or undefined when that cannot be
 * known (no canvas, or no computed font), which means "do not cut".
 *
 * Built from the longhands because Firefox returns an empty `font` shorthand.
 */
export function textMeasurer(element: Element): MeasureText | undefined {
  const ctx = getContext();
  if (!ctx) return undefined;
  const style = getComputedStyle(element);
  if (!style.fontFamily || !style.fontSize) return undefined;
  const font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  const letterSpacing =
    style.letterSpacing && style.letterSpacing !== "normal"
      ? style.letterSpacing
      : "0px";
  const kerning = (style.fontKerning || "auto") as CanvasFontKerning;

  // The context is shared, but one fit runs its measurements synchronously,
  // so setting the font once per measurer is enough.
  ctx.font = font;
  if ("letterSpacing" in ctx) ctx.letterSpacing = letterSpacing;
  if ("fontKerning" in ctx) ctx.fontKerning = kerning;
  return (text) => ctx.measureText(text).width;
}

let fontsVersion = 0;
const listeners = new Set<() => void>();

function bumpFontsVersion() {
  fontsVersion += 1;
  listeners.forEach((listener) => listener());
}

/** For `useSyncExternalStore`: changes whenever a web font finishes loading. */
export function subscribeToFontLoads(listener: () => void) {
  const fonts = typeof document === "undefined" ? undefined : document.fonts;
  if (!fonts) return () => {};
  if (listeners.size === 0) {
    fonts.addEventListener("loadingdone", bumpFontsVersion);
    // Inter loads with `display=swap`, so `ready` can settle before it even
    // starts; `loadingdone` covers the late arrival.
    void fonts.ready.then(bumpFontsVersion);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      fonts.removeEventListener("loadingdone", bumpFontsVersion);
    }
  };
}

export function getFontsVersion() {
  return fontsVersion;
}
